// cart.js
import { qs, qsAll } from '../utils/domHelpers.js';
import { stateManager } from '../utils/stateManager.js';
import { trapFocus, releaseFocus } from '../utils/focusManager.js';

export class Cart {
  constructor(baseQueryEl) {
    this.baseQueryEl = baseQueryEl;
    this.cartDrawer = qs('.cart-drawer');
    this.bindQuantityButtons();
  }

  addToCart(form) {
    const formDataObj = Object.fromEntries(new FormData(form).entries());
    fetch('/cart/add.js', {
      method: 'POST',
      body: JSON.stringify(formDataObj),
      headers: { 'Content-Type': 'application/json' },
    })
      .then((response) => response.json())
      .then(() => this.refreshCartDrawer())
      .catch((err) => {
        console.error('Error adding to cart', err);
      });
  }

  refreshCartDrawer() {
    if (!this.cartDrawer) return;
    fetch('/cart?view=drawer')
      .then((response) => response.text())
      .then((html) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newCartContent = doc.querySelector('.cart-drawer');
        if (newCartContent) {
          this.cartDrawer.innerHTML = newCartContent.innerHTML;
          // Update header cart status.
          const headerCart = qs('.header__cart');
          if (headerCart && headerCart.classList.contains('empty')) {
            headerCart.classList.remove('empty');
          }
          // Update cart count.
          const cartCount = newCartContent.querySelector('.header__cart span');
          const cartCountEl = qs('.header__cart-count');
          if (cartCount && cartCountEl) {
            cartCountEl.textContent = cartCount.textContent;
          }
          // Update cart item total.
          const newCartItemTotal = doc.querySelector('.header__cart span');
          if (newCartItemTotal) {
            const cartItemTotalEl = qs('.header__cart span');
            if (cartItemTotalEl) {
              cartItemTotalEl.textContent = newCartItemTotal.textContent;
            }
          }
        }
        // Update state for the cart globally.
        document.body.classList.add('cart--previewed');
        stateManager.setState('cart');
        // const focusRootEls = [this.cartDrawer, document.querySelector('.section-header')].filter(Boolean);
        const focusRootEls = [document.querySelector('header.header'), document.querySelector('.drawer__inner--cart')];
        trapFocus(focusRootEls);
        // Rebind quantity button events.
        this.bindQuantityButtons();

        // Check for quantity inputs exceeding max and decrement if necessary
        const cartItems = qsAll('.cart-item', this.cartDrawer);
        cartItems.forEach((item) => {
          const quantityInput = item.querySelector('.quantity__input');
          const decrementButton = item.querySelector('.quantity-decrement');
          if (quantityInput && decrementButton) {
            const maxAttr = quantityInput.getAttribute('max');
            const max = parseInt(maxAttr, 10);
            const quantity = parseInt(quantityInput.value, 10);
            if (!isNaN(max) && quantity > max) {
              decrementButton.click();
            }
          }
        });

        const closeTriggers = qsAll('.cart-drawer__close, .cart-drawer__overlay', this.cartDrawer);
        closeTriggers.forEach((el) => {
          el.addEventListener('click', () => {
            releaseFocus();
          });
        });

        this.cartDrawer.addEventListener('click', (e) => {
          const continueShoppingLink = e.target.closest('.cart__continue-shopping');
          if (continueShoppingLink) {
            e.preventDefault();

            releaseFocus();
          }
        });

        const cartNote = qs('#Cart-note', this.cartDrawer);
        if (cartNote) {
          cartNote.addEventListener('change', () => {
            fetch('/cart/update.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
              },
              body: JSON.stringify({
                note: cartNote.value,
              }),
            })
              .then((response) => response.json())
              .then((data) => {
                // console.log('Cart note updated', data);
              })
              .catch((error) => {
                console.error('Error updating cart note:', error);
              });
          });
        }

        const checkoutButton = qs('#CartDrawer-Checkout', this.cartDrawer);
        if (checkoutButton) {
          checkoutButton.addEventListener('click', (e) => {
            if (cartNote && cartNote.value.trim() !== '') {
              e.preventDefault();
              fetch('/cart/update.js', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Accept: 'application/json',
                },
                body: JSON.stringify({
                  note: cartNote.value,
                }),
              })
                .then((response) => response.json())
                .then(() => {
                  checkoutButton.form.submit();
                })
                .catch((error) => {
                  console.error('Error updating cart note before checkout:', error);
                  checkoutButton.form.submit();
                });
            }
          });
        }
      })
      .catch((err) => console.error('Error refreshing cart drawer', err));
  }

  bindEvents() {
    // Handle normal add to cart buttons
    const addToCartButtons = qsAll('.product-form__submit', this.baseQueryEl);
    addToCartButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const form = button.closest('form');
        this.addToCart(form);
        button.textContent = 'Adding to cart...';
        setTimeout(() => {
          button.textContent = 'Add to cart';
        }, 3000);
      });
    });

    // Handle the variant dropdown form with pseudo-select
    const variantDropdownForm = qs('#product-form-main-product-variant-dropdown', this.baseQueryEl);
    if (variantDropdownForm) {
      const pseudoSelectLinks = qsAll('.pseudo__select a', variantDropdownForm);
      const hiddenVariantInput = qs('input[name="id"]', variantDropdownForm);

      pseudoSelectLinks.forEach((link) => {
        link.addEventListener('click', (e) => {
          e.preventDefault();
          const variantId = link.dataset.variantId;
          if (variantId && hiddenVariantInput) {
            hiddenVariantInput.value = variantId;

            // Update label text and close dropdown
            const toggleLabel = qs('.select__toggle', variantDropdownForm);
            const pseudoSelect = qs('.pseudo__select', variantDropdownForm);
            const pseudoSelectWrap = qs('.select__wrap--pseudo', variantDropdownForm);
            if (toggleLabel && pseudoSelect && pseudoSelectWrap) {
              toggleLabel.textContent = link.textContent;
              pseudoSelectWrap.classList.remove('options--open');
              toggleLabel.setAttribute('aria-expanded', 'false');
            }

            // Add to cart and refresh drawer (not full page redirect)
            // this.addToCart(variantDropdownForm);
          }
        });
      });
    }

    // Handle reveal of variant buttons
    const launchVariantButtons = qsAll('.launch-variants', this.baseQueryEl);
    launchVariantButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const revealWrap = document.querySelector('.variants__reveal-wrap');
        if (revealWrap) {
          revealWrap.classList.add('variants__reveal-wrap--active');
        }
        button.remove();
      });
    });

    this.bindQuantityButtons();
  }

  bindQuantityButtons() {
    const quantityButtons = qsAll('.cart-item__quantity-wrapper .quantity__button', this.baseQueryEl);
    quantityButtons.forEach((button) => {
      if (!button.dataset.quantityBound) {
        button.addEventListener('click', (e) => {
          const delta = button.getAttribute('name') === 'plus' ? 1 : -1;
          const input = button.parentElement.querySelector('.quantity__input');
          const maxAttr = input.getAttribute('max') || button.dataset.max;
          const max = parseInt(maxAttr, 10);

          if (
            button.classList.contains('quantity-decrement') &&
            delta < 0 &&
            !isNaN(max) &&
            parseInt(input.value, 10) > max
          ) {
            input.value = max + 1;
            // return;
          }

          this.changeCartItemQuantity(button, delta);
        });
        button.dataset.quantityBound = 'true';
      }
    });
  }

  changeCartItemQuantity(button, delta) {
    const input = button.parentElement.querySelector('.quantity__input');
    const form = button.closest('form');
    const maxAttr = input.getAttribute('max') || button.dataset.max;
    const max = parseInt(maxAttr, 10);
    let newQuantity = parseInt(input.value, 10) + delta;
    // console.log(maxAttr, max, newQuantity);

    if (!isNaN(max) && newQuantity > max) {
      // Optionally show warning if applicable
      const warning = button.closest('.quantity').querySelector('.quantity__warning');
      if (warning) {
        warning.classList.remove('visually-hidden');
      }
      return;
    }

    newQuantity = newQuantity < 1 ? 0 : newQuantity;
    input.value = newQuantity;

    const formData = new FormData(form);
    const bodyData = new URLSearchParams(formData);

    fetch('/cart/update.js', {
      method: 'POST',
      body: bodyData,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    })
      .then((response) => response.json())
      .then(() => this.refreshCartDrawer())
      .catch((err) => console.error('Error updating cart item quantity:', err));
  }
}
